import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Organization } from "types/organization";
import {
  useDeleteOrganizationMutation,
  useGetOrganizationsQuery,
} from "graphql/__generated__/hooks";
import {
  SelectOrganizationThunk,
  setOrganizationsThunk,
} from "reduxStore/organization/thunks";
import { useAppDispatch } from "hooks/useAppDispatch";
import { organizationSelector } from "reduxStore/organization/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import { Avatar, Box, Link as LinkBase, Stack, Tab, Tabs } from "@mui/material";
import { ResponsiveButtonLink } from "components/Button";
import { AdminRoutes } from "config/routes";
import { Link, useNavigate } from "react-router-dom";
import { useNotifications } from "hooks/useNotifications";
import { TabPanel } from "components/TabPanel";
import { OrganizationDomainsPanel } from "./domains/OrganizationDomainsPanel";

export default function OrganizationsList() {
  const [tab, setTab] = useState(0);
  const [deleteOrganizationModal, setDeleteOrganizationModal] = useState(false);
  const [organizationIdToDelete, setOrganizationIdToDelete] = useState("");
  const [deleteOrganization] = useDeleteOrganizationMutation();
  const { loading, error, data, refetch } = useGetOrganizationsQuery();
  const organizations = useAppSelector(organizationSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const setOrganizations = (organizations: Organization[]) => {
    dispatch(setOrganizationsThunk(organizations));
  };

  useEffect(() => {
    setOrganizations((data?.organization as Organization[]) || []);
  }, [data]);

  const handleActionClick = async (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(`/organization/${id}`);
        break;
      case TABLE_ACTIONS.EDIT:
        navigate(`/organization/${id}/edit`);
        break;
      case TABLE_ACTIONS.DELETE:
        setOrganizationIdToDelete(id);
        setDeleteOrganizationModal(true);
        break;
      case TABLE_ACTIONS.SELECT_ORGANIZATION:
        if (!organizations) return;
        await dispatch(
          SelectOrganizationThunk(
            organizations.filter((org: Organization) => org.org_id === id)[0],
          ),
        );
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!organizationIdToDelete || organizationIdToDelete === "") {
      return;
    }
    await deleteOrganization({ variables: { id: organizationIdToDelete } });
    await refetch();
    setOrganizationIdToDelete("");
    notify({
      message: Messages.successOrganizationDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Organizations"
      loading={loading}
      error={error}
      buttonActions={
        tab === 0 ? (
          <ResponsiveButtonLink
            to={AdminRoutes.OrganizationCreate}
            icon={<Add />}
          >
            New Organization
          </ResponsiveButtonLink>
        ) : undefined
      }
    >
      <>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          aria-label="Organizations"
        >
          <Tab
            label="Organizations"
            id="organizations-tab-0"
            aria-controls="organizations-tabpanel-0"
          />
          <Tab
            label="Domains"
            id="organizations-tab-1"
            aria-controls="organizations-tabpanel-1"
          />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <CustomTable
            entities={organizations}
            handleActionClick={handleActionClick}
            fieldToFilter={"name"}
            columns={[
              {
                id: "name",
                label: "Name",
                value: (row: any) => (
                  <Stack direction="row" spacing={2} alignItems={"center"}>
                    <Avatar src={row.logo_url} />
                    <Box>{row.name}</Box>
                  </Stack>
                ),
              },
              {
                id: "org_id",
                value: (row: any) => (
                  <Stack direction="row" spacing={2} alignItems={"center"}>
                    <LinkBase
                      sx={{ cursor: "pointer" }}
                      component={Link}
                      to={`/organization/${row.org_id}`}
                    >
                      View
                    </LinkBase>
                  </Stack>
                ),
              },
              { id: "actions", value: "" },
            ]}
            isSelectable={false}
            allowedMenuItems={[
              TABLE_ACTIONS.VIEW,
              TABLE_ACTIONS.EDIT,
              TABLE_ACTIONS.DELETE,
              TABLE_ACTIONS.SELECT_ORGANIZATION,
            ]}
            extraActionItems={[
              {
                name: TABLE_ACTIONS.SELECT_ORGANIZATION,
                icon: <CheckBoxIcon />,
              },
            ]}
            idColumn={"org_id"}
          />
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <OrganizationDomainsPanel organizations={organizations || []} />
        </TabPanel>

        {deleteOrganizationModal && (
          <ConfirmActionModal
            title={"Heads up!"}
            message={"Are you sure you want to delete this organization?"}
            show={deleteOrganizationModal}
            onConfirm={handleDelete}
            onDismiss={() => {
              setDeleteOrganizationModal(false);
            }}
          />
        )}
      </>
    </MainLayoutContainer>
  );
}
